import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MarketingNav, Footer } from "@/components/Marketing";
import { SlashMark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { LAYOUTS } from "@/lib/themes";
import { Sparkles, Share2, Code2, Presentation, Users, BarChart3, ArrowRight, Check } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

const DEMO = [
  { year: "1943", title: "McCulloch & Pitts", color: "#3A1F6E" },
  { year: "1950", title: "The Turing Test", color: "#6D4AB8" },
  { year: "2012", title: "Deep Learning", color: "#9B7BE0" },
  { year: "2020s", title: "Generative AI", color: "#C4A7F5" },
];

const HeroTimeline = () => (
  <div className="relative rounded-3xl border border-border bg-card p-6 sm:p-10 overflow-hidden shadow-[0_30px_80px_-20px_rgba(58,31,110,0.35)]">
    <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full blur-3xl opacity-40" style={{ background: "#9B7BE0" }} aria-hidden />
    <div className="text-xs uppercase tracking-[0.3em] text-primary mb-6">History of AI</div>
    <div className="relative pl-8">
      <motion.div
        initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} transition={{ duration: 1.3, ease: "easeInOut" }}
        className="absolute left-[7px] top-1 bottom-1 w-[2px] origin-top" style={{ background: "linear-gradient(180deg,#3A1F6E,#C4A7F5)" }} aria-hidden />
      <div className="space-y-6">
        {DEMO.map((d, i) => (
          <motion.div key={d.year} variants={fadeUp} custom={i + 2} initial="hidden" animate="show" className="relative">
            <span className="absolute -left-[29px] top-1 h-3.5 w-3.5 rounded-full ring-4 ring-card" style={{ background: d.color }} />
            <div className="text-xs font-semibold text-primary tracking-widest">{d.year}</div>
            <div className="font-display text-lg font-semibold text-foreground">{d.title}</div>
          </motion.div>
        ))}
      </div>
    </div>
  </div>
);

const Section = ({ children, className = "" }) => (
  <section className={`max-w-7xl mx-auto px-5 sm:px-8 ${className}`}>{children}</section>
);

const Eyebrow = ({ children }) => (
  <div className="text-xs uppercase tracking-[0.3em] text-primary mb-4">{children}</div>
);

export default function Landing() {
  return (
    <div className="bg-background">
      <MarketingNav />

      {/* Hero */}
      <Section className="pt-16 sm:pt-24 pb-20 grain relative">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={0}
              className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-foreground/70 mb-6">
              <SlashMark size={13} color="hsl(var(--primary))" /> Timeline by Mahakali Studios
            </motion.div>
            <motion.h1 variants={fadeUp} initial="hidden" animate="show" custom={1}
              className="font-display text-5xl sm:text-6xl font-semibold leading-[1.02] tracking-tight text-foreground">
              Every story has a<br /><span className="text-primary">timeline.</span>
            </motion.h1>
            <motion.p variants={fadeUp} initial="hidden" animate="show" custom={2}
              className="mt-6 text-lg leading-relaxed text-muted-foreground max-w-lg">
              Create beautiful interactive timelines for history, research, products, companies, classrooms and stories — then publish them anywhere.
            </motion.p>
            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3} className="mt-8 flex flex-wrap gap-3">
              <Link to="/register"><Button size="lg" className="rounded-full h-12 px-7 text-base" data-testid="hero-create-btn">Create a Timeline <ArrowRight size={18} className="ml-1" /></Button></Link>
              <Link to="/discover"><Button size="lg" variant="outline" className="rounded-full h-12 px-7 text-base" data-testid="hero-explore-btn">Explore Timelines</Button></Link>
            </motion.div>
            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={4} className="mt-8 text-sm text-muted-foreground">
              Publish a beautiful timeline in about five minutes. No design skills needed.
            </motion.div>
          </div>
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={2} className="lg:col-span-6">
            <HeroTimeline />
          </motion.div>
        </div>
      </Section>

      {/* Layouts */}
      <Section className="py-20 border-t border-border">
        <Eyebrow>Beautiful layouts</Eyebrow>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
          <h2 className="font-display text-4xl font-semibold tracking-tight text-foreground max-w-xl">One story, many ways to tell it</h2>
          <p className="text-muted-foreground max-w-sm">Switch layouts instantly. Your content stays; the presentation transforms.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          {LAYOUTS.map((l, i) => (
            <motion.div key={l.id} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
              className={`rounded-2xl border border-border bg-card p-7 transition-transform duration-300 hover:-translate-y-1 ${i === 0 ? "md:col-span-6" : i === 1 ? "md:col-span-6" : "md:col-span-4"}`}>
              <div className="h-24 rounded-xl mb-5 relative overflow-hidden" style={{ background: "linear-gradient(135deg,#F2ECFF,#EDE4FF)" }}>
                <LayoutGlyph id={l.id} />
              </div>
              <div className="font-display text-lg font-semibold text-foreground">{l.name}</div>
              <p className="mt-1 text-sm text-muted-foreground">{l.desc}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* AI */}
      <Section className="py-20">
        <div className="rounded-3xl border border-border overflow-hidden bg-primary text-primary-foreground relative grain">
          <div className="absolute -right-20 -bottom-24 h-72 w-72 rounded-full blur-3xl opacity-30 bg-white" aria-hidden />
          <div className="p-10 sm:p-16 grid lg:grid-cols-2 gap-10 items-center relative z-10">
            <div>
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-white/70 mb-4"><Sparkles size={14} /> Create with AI</div>
              <h2 className="font-display text-4xl font-semibold tracking-tight">Describe it. Watch it build itself.</h2>
              <p className="mt-4 text-white/80 leading-relaxed max-w-md">Type a prompt like “the history of artificial intelligence from Alan Turing to modern generative AI” and get a fully editable draft — sections, dates, events, layout and theme.</p>
              <Link to="/register"><Button size="lg" variant="secondary" className="mt-8 rounded-full h-12 px-7" data-testid="ai-cta-btn">Try AI creation</Button></Link>
            </div>
            <div className="rounded-2xl bg-white/10 border border-white/20 p-6 backdrop-blur">
              <div className="text-sm text-white/70 mb-3">Prompt</div>
              <div className="rounded-xl bg-white/10 p-4 text-white/90 text-sm">Create a timeline explaining the history of space exploration from Sputnik to Mars.</div>
              <div className="mt-4 space-y-2">
                {["1957 · Sputnik 1", "1969 · Apollo 11", "2021 · Perseverance on Mars"].map((s) => (
                  <div key={s} className="flex items-center gap-2 text-sm"><Check size={14} className="text-white" /> {s}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Feature grid */}
      <Section className="py-20 border-t border-border">
        <Eyebrow>Publish · Embed · Present · Collaborate</Eyebrow>
        <h2 className="font-display text-4xl font-semibold tracking-tight text-foreground mb-12 max-w-2xl">Everything you need to turn events into experiences</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            [Share2, "Publish anywhere", "Give every timeline a clean, fast, responsive public page with a shareable URL."],
            [Code2, "Embed on any site", "Drop a responsive embed into blogs, docs, and news articles in one line."],
            [Presentation, "Present fullscreen", "Every timeline becomes a keyboard-navigable presentation instantly."],
            [Users, "Collaborate", "Invite editors and viewers to build timelines together."],
            [BarChart3, "Analytics", "See views, visitors and which moments capture the most attention."],
            [Sparkles, "AI copilot", "Expand, shorten, rewrite and organize events without leaving the editor."],
          ].map(([Icon, title, body], i) => (
            <motion.div key={title} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
              className="rounded-2xl border border-border bg-card p-7 transition-transform duration-300 hover:-translate-y-1">
              <div className="h-11 w-11 grid place-items-center rounded-xl bg-accent text-primary mb-5"><Icon size={20} /></div>
              <div className="font-display text-lg font-semibold text-foreground">{title}</div>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{body}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* FAQ */}
      <Section className="py-20">
        <div className="grid lg:grid-cols-3 gap-10">
          <div>
            <Eyebrow>FAQ</Eyebrow>
            <h2 className="font-display text-4xl font-semibold tracking-tight text-foreground">Questions, answered</h2>
          </div>
          <div className="lg:col-span-2">
            <Accordion type="single" collapsible className="w-full">
              {[
                ["Do I need design skills?", "No. Choose a layout and theme, add your events, and Timeline handles the polish for you."],
                ["Can I make timelines with ancient or future dates?", "Yes — from billions of years BCE to future roadmaps. Dates aren't limited to modern calendars."],
                ["How do I share a timeline?", "Publish it and you get a clean shareable URL. You can also embed it or present it fullscreen."],
                ["Can AI build a timeline for me?", "Yes. Describe your topic and AI generates an editable draft with events, dates, layout and theme."],
              ].map(([q, a], i) => (
                <AccordionItem key={i} value={`item-${i}`}>
                  <AccordionTrigger className="text-left font-display text-lg" data-testid={`faq-${i}`}>{q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">{a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </Section>

      {/* Final CTA */}
      <Section className="pb-24">
        <div className="rounded-3xl border border-border bg-card p-12 sm:p-20 text-center relative overflow-hidden grain">
          <SlashMark size={40} color="hsl(var(--primary))" className="mx-auto mb-6" />
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-foreground">Your story already happened.</h2>
          <p className="mt-3 text-2xl font-serif-display italic text-primary">Turn it into a Timeline.</p>
          <Link to="/register"><Button size="lg" className="mt-8 rounded-full h-12 px-8 text-base" data-testid="final-cta-btn">Create your first timeline <ArrowRight size={18} className="ml-1" /></Button></Link>
        </div>
      </Section>

      <Footer />
    </div>
  );
}

const LayoutGlyph = ({ id }) => {
  const c = "#6D4AB8";
  if (id === "horizontal")
    return <div className="absolute inset-0 flex items-center gap-2 px-4"><div className="h-[2px] w-full" style={{ background: c }} /></div>;
  if (id === "cards")
    return <div className="absolute inset-0 grid grid-cols-3 gap-2 p-3">{[0,1,2].map(i=><div key={i} className="rounded bg-white/70" />)}</div>;
  if (id === "fullscreen")
    return <div className="absolute inset-2 rounded-lg bg-white/70" />;
  if (id === "zigzag")
    return <svg className="absolute inset-0 w-full h-full"><polyline points="10,60 60,20 110,60 160,20 210,60" fill="none" stroke={c} strokeWidth="2" /></svg>;
  return <div className="absolute inset-0 flex flex-col justify-center gap-2 px-6"><div className="h-[2px] w-2/3" style={{background:c}} /><div className="h-[2px] w-1/2" style={{background:c}} /><div className="h-[2px] w-3/5" style={{background:c}} /></div>;
};
